"use client";

import { useState } from "react";

function Icon({ name, className = "", fill = false }: { name: string; className?: string; fill?: boolean }) {
  return <span className={`material-symbols-outlined ${fill ? "fill" : ""} ${className}`}>{name}</span>;
}

type QA = { qAr: string; qEn: string; aAr: string; aEn: string };

const TRAVELERS: QA[] = [
  {
    qAr: "ما هي سياسة الإلغاء في منازل الدرعية؟",
    qEn: "What is the cancellation policy?",
    aAr: "تختلف سياسة الإلغاء حسب الوحدة أو التجربة. ستجد تفاصيل الاسترداد بوضوح في صفحة الحجز قبل إتمام الدفع.",
    aEn: "The policy varies by stay or experience. You'll see the refund details clearly on the booking page before you pay.",
  },
  {
    qAr: "كيف يمكنني تعديل تواريخ حجزي؟",
    qEn: "How do I change my booking dates?",
    aAr: "من صفحة «حجوزاتي» اختر الحجز ثم اضغط تعديل. إن كانت التواريخ الجديدة متاحة سيتم تحديث الحجز فوراً.",
    aEn: "From “My bookings”, open the booking and tap Edit. If the new dates are available, it updates instantly.",
  },
  {
    qAr: "ما هي طرق الدفع المتاحة؟",
    qEn: "Which payment methods are available?",
    aAr: "ندعم البطاقات الائتمانية ومدى ووسائل الدفع المحلية عبر بوابة دفع مشفّرة وآمنة.",
    aEn: "We support credit cards, Mada and local payment methods through a secure, encrypted gateway.",
  },
  {
    qAr: "هل هناك رسوم خدمة إضافية؟",
    qEn: "Are there extra service fees?",
    aAr: "تظهر جميع الرسوم بشفافية في ملخّص الحجز قبل الدفع، بدون أي مبالغ خفية.",
    aEn: "All fees are shown transparently in the booking summary before payment — no hidden charges.",
  },
];

const HOSTS: QA[] = [
  {
    qAr: "كيف أصبح مضيفاً أو مزوّد خدمة؟",
    qEn: "How do I become a host or provider?",
    aAr: "توجّه إلى صفحة «أضف وحدتك» أو تواصل معنا، وسيساعدك فريقنا في إدراج عرضك خلال وقت قصير.",
    aEn: "Go to the “List your property” page or contact us, and our team will help you get listed quickly.",
  },
  {
    qAr: "متى وكيف أستلم مستحقاتي؟",
    qEn: "When and how do I get paid?",
    aAr: "تُحوَّل مستحقاتك إلى حسابك البنكي بعد تسجيل وصول الضيف وفق جدول الدفعات الموضّح في لوحة المضيف.",
    aEn: "Payouts are sent to your bank account after guest check-in, per the schedule shown in your host dashboard.",
  },
  {
    qAr: "كيف أدير التقويم والتوفّر؟",
    qEn: "How do I manage my calendar and availability?",
    aAr: "من لوحة التحكم يمكنك حظر التواريخ وتحديد الأسعار والحد الأدنى لليالي بسهولة.",
    aEn: "From your dashboard you can block dates, set pricing and minimum nights easily.",
  },
  {
    qAr: "ما هي رسوم المنصّة على المضيفين؟",
    qEn: "What is the platform fee for hosts?",
    aAr: "تُحتسب رسوم خدمة بسيطة على كل حجز، وتظهر بوضوح في تفاصيل كل عملية.",
    aEn: "A small service fee applies per booking and is shown clearly in each transaction's details.",
  },
];

function Accordion({ items, isAr }: { items: QA[]; isAr: boolean }) {
  const t = (a: string, e: string) => (isAr ? a : e);
  const [open, setOpen] = useState<number | null>(0);
  return (
    <div className="space-y-3">
      {items.map((f, i) => {
        const isOpen = open === i;
        return (
          <div key={i} className="rounded-2xl border border-clay-100 bg-surface overflow-hidden">
            <button onClick={() => setOpen(isOpen ? null : i)} className="w-full flex items-center justify-between gap-4 p-5 text-start">
              <span className="font-bold text-ink-900">{t(f.qAr, f.qEn)}</span>
              <Icon name="expand_more" className={`text-clay-600 shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`} />
            </button>
            {isOpen && <div className="px-5 pb-5 -mt-1 text-sm text-ink-500 leading-loose">{t(f.aAr, f.aEn)}</div>}
          </div>
        );
      })}
    </div>
  );
}

export default function HelpFAQ({ isAr, query }: { isAr: boolean; query: string }) {
  const t = (a: string, e: string) => (isAr ? a : e);
  const [tab, setTab] = useState<"travelers" | "hosts">("travelers");
  const q = query.trim().toLowerCase();

  const match = (arr: QA[]) =>
    arr.filter((f) => !q || f.qAr.includes(q) || f.qEn.toLowerCase().includes(q) || f.aAr.includes(q) || f.aEn.toLowerCase().includes(q));

  const items = tab === "travelers" ? match(TRAVELERS) : match(HOSTS);

  return (
    <section className="py-16">
      <div className="container mx-auto px-6 max-w-3xl">
        <h2 className="font-display text-2xl font-bold text-ink-900 mb-6 text-center">
          {t("الأسئلة الشائعة", "Frequently asked questions")}
        </h2>

        {/* Tabs */}
        <div className="flex items-center justify-center mb-8">
          <div className="inline-flex rounded-full bg-clay-50 p-1 border border-clay-100">
            {([
              { k: "travelers", ar: "للمسافرين", en: "For travelers" },
              { k: "hosts", ar: "للمضيفين", en: "For hosts" },
            ] as const).map((tb) => (
              <button
                key={tb.k}
                onClick={() => setTab(tb.k)}
                className={`rounded-full px-6 py-2 text-sm font-bold transition-colors ${
                  tab === tb.k ? "bg-clay-600 text-white shadow" : "text-ink-500 hover:text-clay-600"
                }`}
              >
                {t(tb.ar, tb.en)}
              </button>
            ))}
          </div>
        </div>

        {items.length === 0 ? (
          <p className="text-center text-ink-500">{t("لا توجد أسئلة مطابقة", "No matching questions")}</p>
        ) : (
          <Accordion items={items} isAr={isAr} />
        )}
      </div>
    </section>
  );
}
